import { COLORS } from "@msuite/utils/colors";
import { fillPatterns } from "@msuite/utils/schemas";
import { generateId } from "@msuite/utils/strings";
import type {
	Annotation,
	DimensionAnnotation,
	DimensionChainStyle,
	DimensionDefaults,
	DragKind,
	DragPoint,
	ImageAnnotatorImage,
	Nullable,
	PendingState,
	Point,
	PolygonDefaults,
	TextAnnotation,
	TextDefaults,
	ToolName,
} from "@msuite/utils/types";
import React, { useCallback, useRef, useState } from "react";
import type { GestureResponderEvent, LayoutChangeEvent } from "react-native";
import type { Svg } from "react-native-svg";
import { TOOL } from "./constants";
import { getTextAnnotationBox } from "./text";
import { getPointDistance, getRnSvgPoint, getWebSvgPoint } from "./utils";

/** Props Interface */
interface UseImageAnnotatorProps {
	image: Nullable<ImageAnnotatorImage>;
	nativeSvgRef?: React.RefObject<Svg | null>;
	webSvgRef?: React.RefObject<SVGSVGElement | null>;
	isReadOnly: boolean;
}

export type ImageAnnotatorController = ReturnType<typeof useImageAnnotator>;

export function useImageAnnotator({
	image,
	nativeSvgRef,
	webSvgRef,
	isReadOnly,
}: UseImageAnnotatorProps) {
	/** Data State */
	const [annotations, setAnnotations] = useState<Annotation[]>([]);
	const [showAnnotations, setShowAnnotations] = useState<boolean>(true);
	const [tool, setTool] = useState(TOOL.SELECT);
	const [selectionId, setSelectionId] = useState<string | null>(null);
	const [textEditId, setTextEditId] = useState<string | null>(null);
	const [scale, setScale] = useState(1);

	/** Interaction State */
	const [polygonPoints, setPolygonPoints] = useState<Point[]>([]);
	const [dimensionPoint, setDimensionPoint] = useState<Point | null>(null);
	const [dimensionChainStyle, setDimensionChainStyle] = useState<DimensionChainStyle | null>(null);
	const [mouse, _setMouse] = useState<Point | null>(null);
	const [pendingState, setPendingState] = useState<PendingState | null>(null);
	const [_dragPoint, setDragPoint] = useState<DragPoint | null>(null);

	/** Settings State */
	const [polygonDefault] = useState<PolygonDefaults>({
		strokeColor: "red",
		strokeWidth: 3,
		fillColor: "rgba(255,0,0,0.35)",
		fillPattern: fillPatterns.at(0)!,
		opacity: 1,
		label: "",
	});
	const [dimensionDefault] = useState<DimensionDefaults>({
		text: "",
		unit: "meters",
		strokeWidth: 1,
		strokeColor: COLORS.at(0)!,
		markerColor: COLORS.at(0)!,
	});
	const [textDefault] = useState<TextDefaults>({
		text: "",
		fontSize: 16,
		strokeWidth: 1,
		strokeColor: COLORS.at(0)!,
	});

	/** Ref */
	const annotationsRef = useRef<Annotation[]>([]);
	const [svgLayout, setSvgLayout] = useState<{ width: number; height: number } | null>(null);

	/** Image units to rendered pixels. Declared here because the callbacks below depend on it. */
	const baseScale = svgLayout && image ? svgLayout.width / image.width : 1;

	/** Callbacks */
	async function saveAnnotations(next: Annotation[]) {
		if (isReadOnly) return;
		setAnnotations(next);
		annotationsRef.current = next;
	}

	const handleOnUpdateSelection = useCallback(
		(patch: Partial<Annotation>) => {
			if (isReadOnly) return;
			saveAnnotations(
				annotations.map((a) => (a.id === selectionId ? ({ ...a, ...patch } as Annotation) : a)),
			);
		},
		[selectionId, annotations, saveAnnotations, isReadOnly],
	);

	const handleOnSvgLayout = useCallback(
		(e: LayoutChangeEvent | { width: number; height: number }) => {
			if ("nativeEvent" in e) {
				setSvgLayout({
					width: e.nativeEvent.layout.width,
					height: e.nativeEvent.layout.height,
				});
			} else {
				setSvgLayout({ width: e.width, height: e.height });
			}
		},
		[],
	);

	const getPoint = useCallback(
		(e: React.MouseEvent | GestureResponderEvent): Point => {
			if (nativeSvgRef && "nativeEvent" in e) {
				const nativeEvent = e.nativeEvent as GestureResponderEvent["nativeEvent"];
				const matrix = nativeSvgRef.current?.getScreenCTM();
				const locationX = nativeEvent.locationX;
				const locationY = nativeEvent.locationY;
				const isIdentity = matrix?.a === 1 && matrix?.d === 1 && matrix?.e === 0 && matrix?.f === 0;
				if (isIdentity && image && svgLayout) {
					return {
						x: locationX * (image.width / svgLayout.width),
						y: locationY * (image.height / svgLayout.height),
					};
				}
				return getRnSvgPoint(locationX, locationY, matrix || null);
			} else if (webSvgRef?.current) {
				return getWebSvgPoint(webSvgRef.current, e);
			}

			return { x: 0, y: 0 };
		},
		[image],
	);

	const handleOnSvgClick = useCallback(
		async (e: React.MouseEvent | GestureResponderEvent) => {
			if (isReadOnly) return;
			if (!image) return;
			if (pendingState?.phase) return;
			const p = getPoint(e);

			requestAnimationFrame(() => {
				if (tool === TOOL.POLYGON) {
					const thr = 20 / (baseScale * (scale || 1));
					if (polygonPoints.length >= 3 && getPointDistance(p, polygonPoints[0]!) < thr) {
						const annotation: Annotation = {
							id: generateId(),
							type: "polygon",
							points: [...polygonPoints],
							...polygonDefault,
						};
						handleOnAddAnnotation(annotation);
					} else {
						if (polygonPoints.length === 0) {
							setPolygonPoints([p]);
						} else {
							setPolygonPoints((prev) => [...prev, p]);
						}
					}
				} else if (tool === TOOL.DIMENSION) {
					if (!dimensionPoint) {
						setDimensionPoint(p);
					} else {
						const annotation: DimensionAnnotation = {
							id: generateId(),
							type: "dimension",
							start: dimensionPoint,
							end: p,
							offset: 40,
							tilt: 0,
							...dimensionDefault,
							/** A chained dimension keeps the look of the one it was started from. */
							...(dimensionChainStyle ?? {}),
						};
						handleOnAddAnnotation(annotation);
					}
				} else if (tool === TOOL.TEXT) {
					const panel: TextAnnotation = {
						id: generateId(),
						type: "text",
						x: p.x,
						y: p.y,
						...textDefault,
					};
					/**
					 * The leader line leaves the panel's bottom edge, so the point it aims at starts
					 * below the panel — otherwise the line crosses back over the label. The gap is in
					 * screen pixels, like every other authored length.
					 */
					const box = getTextAnnotationBox(panel, baseScale);
					handleOnAddAnnotation({
						...panel,
						callout: { x: p.x, y: box.y + box.height + 40 / baseScale },
					});
				} else {
					setSelectionId(null);
					setTextEditId(null);
				}
			});
		},
		[
			tool,
			polygonPoints,
			dimensionPoint,
			pendingState,
			annotations,
			polygonDefault,
			dimensionDefault,
			dimensionChainStyle,
			textDefault,
			getPoint,
			scale,
			baseScale,
			saveAnnotations,
			isReadOnly,
		],
	);

	function handleOnAddAnnotation(annotation: Annotation) {
		if (isReadOnly) return;
		saveAnnotations([...annotations, annotation]);
		setSelectionId(annotation.id);
		setTool(TOOL.SELECT);
		setDimensionPoint(null);
		setDimensionChainStyle(null);
		setPolygonPoints([]);
	}

	/**
	 * Arm a new dimension that starts at one arrow tip of an existing one, so a run of measurements
	 * can be chained tip to tip. The next click on the canvas sets the far end.
	 *
	 * The chained dimension carries no offset of its own: its dimension line *is* the span from
	 * the arrow tip it was started at, which is what makes the new arrow begin exactly where the
	 * previous one ends. Only the first dimension of a run stands off the object it measures.
	 *
	 * @param tip - The arrow tip the new dimension starts from.
	 * @param source - The dimension whose colour, width and unit the new one inherits.
	 */
	function handleOnStartDimensionChain(tip: Point, source: DimensionAnnotation) {
		if (isReadOnly) return;
		setSelectionId(null);
		setPendingState(null);
		setPolygonPoints([]);
		setTool(TOOL.DIMENSION);
		setDimensionPoint(tip);
		setDimensionChainStyle({
			unit: source.unit,
			offset: 0,
			tilt: 0,
			strokeWidth: source.strokeWidth,
			strokeColor: source.strokeColor,
			markerColor: source.markerColor,
		});
	}

	/**
	 * Open the in-place editor on a text annotation, so its content is typed on the image itself
	 * rather than in the toolbar. Selecting it as well keeps the toolbar in agreement about what
	 * is being worked on.
	 *
	 * @param annotationId - The text annotation to edit.
	 */
	function handleOnStartTextEdit(annotationId: string) {
		if (isReadOnly) return;
		setSelectionId(annotationId);
		setTextEditId(annotationId);
	}

	/**
	 * Close the in-place editor. The label itself needs no committing — it is written straight to
	 * the annotation as it is typed.
	 */
	function handleOnEndTextEdit() {
		setTextEditId(null);
	}

	const handleOnDrag = useCallback(
		(annotationId: string, kind: DragKind, p: Point, index?: number) => {
			if (isReadOnly) return;
			setAnnotations((prev) => {
				const next = prev.map((a) => {
					if (a.id !== annotationId) return a;
					if (kind === "poly" && a.type === "polygon") {
						const pts = [...a.points];
						pts[index!] = p;
						return { ...a, points: pts };
					}
					if (kind === "dim_start" && a.type === "dimension") return { ...a, start: p };
					if (kind === "dim_end" && a.type === "dimension") return { ...a, end: p };
					if (kind === "txt_pos" && a.type === "text") return { ...a, x: p.x, y: p.y };
					if (kind === "txt_callout" && a.type === "text") return { ...a, callout: p };
					return a;
				});
				annotationsRef.current = next;
				return next;
			});
		},
		[tool, polygonPoints, dimensionPoint, pendingState, getPoint, isReadOnly],
	);

	const handleCallout = useCallback(
		(e: React.MouseEvent | GestureResponderEvent) => {
			if (isReadOnly) return;
			if (pendingState?.phase !== "callout") return;
			const final = { ...pendingState?.annotation, callout: getPoint(e) } as Annotation;
			saveAnnotations([...annotations, final]);
			setPendingState(null);
			setTool(TOOL.SELECT);
		},
		[pendingState, annotations, getPoint, saveAnnotations, isReadOnly],
	);

	const handleOnChangeTool = useCallback(
		(t: ToolName) => {
			if (isReadOnly) return;
			setTool(t);
			setPolygonPoints([]);
			setDimensionPoint(null);
			setDimensionChainStyle(null);
			setTextEditId(null);
			setPendingState(null);
			setDragPoint(null);
		},
		[isReadOnly],
	);

	function handleOnContainerPress(e: React.MouseEvent | GestureResponderEvent) {
		if (isReadOnly) return;
		if (pendingState?.phase === "callout") {
			handleCallout(e);
		} else {
			handleOnSvgClick(e);
		}
	}

	function handleOnDeleteSelection() {
		if (isReadOnly) return;
		if (!selectionId) return;
		saveAnnotations(annotations.filter((a) => a.id !== selectionId));
		setSelectionId(null);
		setTextEditId(null);
	}

	function handleOnDeleteAnnotation(annotationId: string) {
		if (isReadOnly) return;
		saveAnnotations(annotations.filter((a) => a.id !== annotationId));
		if (selectionId === annotationId) {
			setSelectionId(null);
		}
		if (textEditId === annotationId) {
			setTextEditId(null);
		}
	}

	function getSelectedAnnotation(): Annotation | null {
		if (isReadOnly) return null;
		if (!selectionId) return null;
		return annotations.find((a) => a.id === selectionId) || null;
	}

	function handleOnMoveSelectionToFront() {
		if (isReadOnly) return;
		if (!selectionId) return;
		const index = annotations.findIndex((a) => a.id === selectionId);
		if (index === -1 || index === annotations.length - 1) return;
		const next = [...annotations];
		const [selected] = next.splice(index, 1);
		if (!selected) return;
		next.push(selected);
		saveAnnotations(next);
	}

	function handleOnMoveSelectionToBack() {
		if (isReadOnly) return;
		if (!selectionId) return;
		const index = annotations.findIndex((a) => a.id === selectionId);
		if (index === -1 || index === 0) return;
		const next = [...annotations];
		const [selected] = next.splice(index, 1);
		if (!selected) return;
		next.unshift(selected);
		saveAnnotations(next);
	}

	function handleOnToggleShowAnnotations() {
		setShowAnnotations(!showAnnotations);
		setTextEditId(null);
	}

	/** Computed */
	const hasSelection = selectionId !== null;
	const selectionCanGoFront =
		hasSelection && annotations.findIndex((a) => a.id === selectionId) !== annotations.length - 1;
	const selectionCanGoBack =
		hasSelection && annotations.findIndex((a) => a.id === selectionId) !== 0;

	return {
		annotations,
		setAnnotations,
		showAnnotations,
		tool,
		setTool,
		selectionId,
		setSelectionId,
		textEditId,
		polygonPoints,
		dimensionPoint,
		dimensionChainStyle,
		mouse,
		scale,
		baseScale,
		pendingState,
		polygonDefault,
		setScale,
		dimensionDefault,
		textDefault,
		hasSelection,
		handleOnSvgLayout,
		handleOnSvgClick,
		handleOnDrag,
		handleOnStartDimensionChain,
		handleOnStartTextEdit,
		handleOnEndTextEdit,
		handleCallout,
		handleOnUpdateSelection,
		handleOnChangeTool,
		handleOnContainerPress,
		handleOnDeleteSelection,
		handleOnDeleteAnnotation,
		getSelectedAnnotation,
		selectionCanGoFront,
		selectionCanGoBack,
		handleOnMoveSelectionToFront,
		handleOnMoveSelectionToBack,
		isReadOnly,
		setShowAnnotations,
		handleOnToggleShowAnnotations,
	};
}
